import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetColours } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import ColourHttpService from '../../../../services/http/masterData/colourHttpService';
import ColourForm from './form/ColourForm';
import { setColourMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import ColourModelHelper from '../../../../@types/model/masterData/colour/colourModelHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import { IColour } from '../../../../@types/model/masterData/colour/colour';
import { IColourFormValues } from '../../../../@types/model/masterData/colour/colourFormValues';
import UserHelper from '../../../../services/helper/userHelper';
import { useLocation } from 'react-router-dom';

interface IColourScreenProps {
    selectedCommodity : ICommodity | null;
}

const ColourScreen = (props : IColourScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const colours = useAppSelector(x => x.masterData.colours);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedColour, setSelectedColour] = useState<IColour | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingColour, setDeletingColour] = useState<IColour | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/
    
    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await ColourHttpService.getColourData();
                dispatch(dataSetColours(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading colours.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IColourFormValues) => {
        setIsLoading(true);

        try {
            const upsert = ColourModelHelper.createUpsert(values);
            const res = await ColourHttpService.addOrUpdateColour(upsert);
            const updatedList = upsertArrayElement(colours, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetColours(updatedList));
            await setColourMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedColour ? 'Colour updated successfully.' : 'Colour added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating colour data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeColour = async () => {
        if (!deletingColour) return;
        const updatedColour = { ...deletingColour, isActive: false };
        setIsLoading(true);
        try {
            const res = await ColourHttpService.deleteColour(updatedColour.id);
            const updatedList = upsertArrayElement(colours, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetColours(updatedList));
            await setColourMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Colour deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting colour.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedColour(null);
    };

    const edit = (selectedRow : IColour) => {
        setSelectedColour(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IColour) : void => {
        setDeletingColour(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingColour(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Colour', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : IColour) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Colour', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatCommodities = useCallback((commodityIds : Array<number>) => {
        let string = commodities
            .filter(x => commodityIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
        if (string.length > 85) {
            string = string.substring(0, 86) + '...';
        }
        return string;
    }, [commodities]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return ColourModelHelper.createFormValues(selectedColour, commodities, props.selectedCommodity);
        }
    }, [selectedColour, isDialogOpen, commodities, props.selectedCommodity]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return props.selectedCommodity
            ? colours.filter(x => x.commodityIds.some(y => y === props.selectedCommodity?.id))
            : colours;
    }, [props.selectedCommodity, colours]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <div className={'fdc hfill'}>
                <Card className={'fdc hfill'}>
                    <CustomTable<IColour>
                        
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                        addTooltip={addButtonTooltip()}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Colour', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IColour) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                
                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Commodities', field: 'commodityIds', formatFunction: formatCommodities, width: 300, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={rows}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={340}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedColour ? 'Edit Colour' : 'Add Colour'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <ColourForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Colour'}
                open={!!deletingColour}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeColour}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default ColourScreen;
